// /frontend/src/pages/dashboard/post/pinterest/PinterestPostService.js
// Greatly Social – Pinterest helper (v3)
// • Accepts one or many board IDs
// • Sends field names exactly as /api/posting/pinterest/publish expects
// • Enhanced error handling for better user feedback
import axios from 'axios';

/**
 * publishPinterestPost
 * @param {string}   pinterestId            – the Pinterest user ID stored in pinterest_accounts
 * @param {string[]} boardIds               – array of board_id strings (use single-element array for one board)
 * @param {string}   title                  – Pin title
 * @param {string}   description            – Pin description
 * @param {string}   mediaUrl               – Public https URL of the image (or null for text-only, but GS always sends an image)
 * @returns {Promise<Object>}              – backend JSON (success / results array)
 */
export async function publishPinterestPost(
  pinterestId,
  boardIds,
  title,
  description,
  mediaUrl
) {
  try {
    // Validation
    if (!pinterestId) {
      throw new Error('Pinterest account ID is required');
    }
    if (!boardIds || (Array.isArray(boardIds) && boardIds.length === 0)) {
      throw new Error('At least one Pinterest board must be selected');
    }
    if (!title) {
      throw new Error('Pin title is required');
    }
    if (!mediaUrl) {
      throw new Error('Pinterest requires an image to create a pin');
    }
    
    // normalise: backend field name is boardId (singular) but accepts array
    const body = {
      pinterestId,
      boardId: Array.isArray(boardIds) ? boardIds : [boardIds],
      title,
      description,
      mediaUrl,
    };
    
    const { data } = await axios.post('/api/posting/pinterest/publish', body, {
      withCredentials: true, // send session cookie
    });
    
    console.log('publishPinterestPost success:', data);
    return data;
    
  } catch (err) {
    console.error('publishPinterestPost error:', err?.response?.data || err);
    
    // Extract the actual error message from various possible locations
    let errorMessage = 'Failed to post to Pinterest';
    
    // If this is our custom validation error, preserve it
    if (!err.response && err.message) {
      errorMessage = err.message;
    } else if (err.response?.data) {
      // Check multiple possible error locations in order of preference
      if (err.response.data.error && typeof err.response.data.error === 'string') {
        errorMessage = err.response.data.error;
      } else if (err.response.data.error?.message) {
        errorMessage = err.response.data.error.message;
      } else if (err.response.data.message) {
        errorMessage = err.response.data.message;
      } else if (err.response.data.details) {
        errorMessage = err.response.data.details;
      } else if (err.response.data.code) {
        // Pinterest specific error codes
        errorMessage = getPinterestErrorByCode(err.response.data.code, err.response.data.message);
      } else if (err.response.data.error_summary) {
        // Pinterest API v5 error format
        errorMessage = err.response.data.error_summary;
      } else if (err.response.data.data?.message) {
        // Another Pinterest error format
        errorMessage = err.response.data.data.message;
      } else if (typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
    } else if (err.response?.status) {
      // Handle HTTP status codes
      errorMessage = getPinterestErrorByStatus(err.response.status, err.message);
    } else if (err.message) {
      errorMessage = err.message;
    }
    
    // Throw error with the extracted message
    throw new Error(errorMessage);
  }
}

/**
 * Maps Pinterest error codes to user-friendly messages
 */
function getPinterestErrorByCode(code, fallbackMessage) {
  const errorMap = {
    1: 'Pinterest authorization failed. Please reconnect your account.',
    2: 'Pinterest authentication expired. Please reconnect your account.',
    3: 'Pinterest permission denied. Please reconnect with proper permissions.',
    7: 'Pinterest API temporarily unavailable. Please try again later.',
    8: 'Pinterest rate limit exceeded. Please wait before posting again.',
    9: 'Pinterest API limit reached. Daily posting quota exceeded.',
    11: 'Pinterest parameter missing. Please check all required fields.',
    50: 'Pinterest board not found. Please select a valid board.',
    51: 'Pinterest user not found. Account may be restricted.',
    52: 'Pinterest board access denied. Check board permissions.',
    100: 'Invalid Pinterest image. Please check image format and size.',
    101: 'Pinterest image too large. Please use a smaller image.',
    102: 'Pinterest image download failed. Please check the image URL.',
    103: 'Pinterest unsupported image format. Use JPG, PNG, or GIF.',
    108: 'Pinterest description too long. Please shorten your description.',
    109: 'Pinterest title too long. Maximum 100 characters allowed.',
    400: 'Invalid Pinterest request. Please check your content.',
    401: 'Pinterest unauthorized. Please reconnect your account.',
    403: 'Pinterest forbidden. Account may not have posting permissions.',
    404: 'Pinterest resource not found. Board or account may be deleted.',
    429: 'Too many Pinterest requests. Please slow down and try again.',
    500: 'Pinterest server error. Please try again later.',
    502: 'Pinterest server error. Please try again later.',
    503: 'Pinterest service unavailable. Please try again later.',
  };
  
  return errorMap[code] || fallbackMessage || `Pinterest error (code: ${code})`;
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
function getPinterestErrorByStatus(status, fallbackMessage) {
  const statusMap = {
    400: 'Invalid Pinterest request. Please check your pin details.',
    401: 'Pinterest authentication failed. Please reconnect your account.',
    403: 'Pinterest access forbidden. Check your account permissions.',
    404: 'Pinterest resource not found. Board may have been deleted.',
    429: 'Pinterest rate limit hit. Please wait a moment before trying again.',
    500: 'Pinterest server error. Please try again later.',
    502: 'Pinterest server error. Please try again later.',
    503: 'Pinterest service unavailable. Please try again later.',
  };
  
  return statusMap[status] || fallbackMessage || `Pinterest error (status: ${status})`;
}

/**
 * Helper function to get user-friendly error messages
 * Can be used by UI components to show better error dialogs
 */
export function getPinterestErrorMessage(error) {
  const message = error.message || error.toString();
  const lowerMessage = message.toLowerCase();
  
  // Map technical errors to user-friendly messages with actions
  if (lowerMessage.includes('authentication') || lowerMessage.includes('authorization') || lowerMessage.includes('expired')) {
    return {
      title: 'Connection Issue',
      message: 'Your Pinterest connection has expired. Please reconnect your account in Settings.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('permission')) {
    return {
      title: 'Permission Required',
      message: 'Missing Pinterest permissions. Please reconnect with all required permissions.',
      action: 'reconnect'
    };
  }
  
  if (lowerMessage.includes('board not found') || lowerMessage.includes('board access denied')) {
    return {
      title: 'Board Issue',
      message: 'The selected board is not available. It may have been deleted or you lost access.',
      action: 'select_different_board'
    };
  }
  
  if (lowerMessage.includes('rate limit') || lowerMessage.includes('too many')) {
    return {
      title: 'Slow Down',
      message: 'You\'ve reached Pinterest\'s posting limit. Please wait a few minutes before trying again.',
      action: 'wait'
    };
  }
  
  if (lowerMessage.includes('daily posting quota') || lowerMessage.includes('api limit reached')) {
    return {
      title: 'Daily Limit Reached',
      message: 'You\'ve reached your daily Pinterest posting limit. Try again tomorrow.',
      action: 'wait_daily'
    };
  }
  
  if (lowerMessage.includes('image too large')) {
    return {
      title: 'Image Too Large',
      message: 'Your image is too large for Pinterest. Please use a smaller file.',
      action: 'resize'
    };
  }
  
  if (lowerMessage.includes('unsupported image format')) {
    return {
      title: 'Invalid Format',
      message: 'Pinterest doesn\'t support this image format. Use JPG, PNG, or GIF.',
      action: 'convert'
    };
  }
  
  if (lowerMessage.includes('title too long')) {
    return {
      title: 'Title Too Long',
      message: 'Pin title is too long. Maximum 100 characters allowed.',
      action: 'edit_title'
    };
  }
  
  if (lowerMessage.includes('description too long')) {
    return {
      title: 'Description Too Long',
      message: 'Pin description is too long. Please shorten it.',
      action: 'edit_description'
    };
  }
  
  if (lowerMessage.includes('image download failed')) {
    return {
      title: 'Image Load Failed',
      message: 'Pinterest couldn\'t download your image. Please check the URL or try a different image.',
      action: 'check_image'
    };
  }
  
  // Default
  return {
    title: 'Posting Failed',
    message: message,
    action: null
  };
}